import React, { useState, useEffect } from 'react';
import api from '../api';

const Contact = () => {
  const [settings, setSettings] = useState({ site_name: 'ecom', accent_color: '#c9a96e' });
  const [form, setForm]         = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus]     = useState(null); // 'success' | 'error'
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    api.get('/api/settings').then(({ data }) => {
      setSettings(s => ({ ...s, ...data.data }));
    }).catch(() => {});
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.post('/api/contact', form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '9rem 1.5rem 5rem',
        marginTop: 'var(--nav-h)',
      }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 700, marginBottom: '1rem' }}>
          Contact Us
        </h1>
        <p style={{ color: '#aaa', maxWidth: 500, margin: '0 auto', fontSize: '1.05rem' }}>
          Have a question? We'd love to hear from you. Send us a message and we'll get back to you within 24 hours.
        </p>
      </section>

      {/* Main content */}
      <section className="page">
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'start' }}>

          {/* Contact info */}
          <div>
            <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', marginBottom: '1.5rem' }}>Get in touch</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', lineHeight: 1.7 }}>
              Whether you have a question about an order, a product, or anything else — our team is ready to help.
            </p>

            {[
              { icon: '📧', label: 'Email', value: settings.contact_email || 'support@' + (settings.site_name || 'ecom').toLowerCase().replace(/\s/g, '') + '.com' },
              { icon: '📞', label: 'Phone', value: settings.contact_phone || '+1 (555) 000-0000' },
              { icon: '📍', label: 'Address', value: settings.contact_address || '123 Commerce Street, City, Country' },
              { icon: '🕐', label: 'Hours', value: 'Mon – Fri, 9 AM – 6 PM' },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 'var(--radius)',
                  background: '#f5f0ea', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0,
                }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '.2rem' }}>
                    {label}
                  </div>
                  <div style={{ color: 'var(--text)', fontSize: '.95rem' }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="card" style={{ padding: '2rem' }}>
            <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', marginBottom: '1.5rem' }}>Send a message</h2>

            {status === 'success' && (
              <div className="alert alert-success">
                Message sent! We'll get back to you within 24 hours.
              </div>
            )}
            {status === 'error' && (
              <div className="alert alert-error">
                Something went wrong. Please try again or email us directly.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input className="form-control" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input className="form-control" name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@example.com" required />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <select className="form-control" name="subject" value={form.subject} onChange={handleChange}>
                    <option value="">-- Sélectionnez un sujet --</option>
                    <option value="Retard de livraison">Retard de livraison</option>
                    <option value="Problème lors de la livraison">Problème lors de la livraison</option>
                    <option value="Produit endommagé">Produit endommagé</option>
                    <option value="Remboursement / Retour">Remboursement / Retour</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea className="form-control" name="message" value={form.message} onChange={handleChange} placeholder="Tell us how we can help..." rows={5} required />
              </div>

              <button className="btn btn-accent btn-full" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="footer" style={{ position: 'relative' }}>
        © {new Date().getFullYear()} <span>{settings.site_name}</span>. All rights reserved.
        <a href="/admin/login" style={{ position: 'absolute', bottom: 0, right: 0, padding: '.5rem 1rem', color: '#1a1a1a', fontSize: '.8rem', textDecoration: 'none' }}>Admin</a>
      </footer>
    </>
  );
};

export default Contact;
